import { faCircleHalfStroke } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { AppearanceSwitch, VibeSwitch } from "./AppearanceSwitch";

/**
 * The theme control in the header: how light the product is, and how soft.
 *
 * Both axes are theme values, so nothing here knows what it is changing — the vibe
 * moves a handful of radii and every corner in the product follows.
 */
function ThemePicker({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className={"relative " + (className ?? "")} ref={root}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="dialog"
        data-testid="theme-picker-trigger"
        className="flex items-center justify-center w-9 h-9 rounded-avatar text-muted hover:text-text hover:bg-tint-hover"
      >
        <FontAwesomeIcon icon={faCircleHalfStroke} />
        <span className="sr-only"><FormattedMessage id="appearance__label" /></span>
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 z-[220] w-[264px] flex flex-col gap-4 p-4 rounded-panel bg-panel shadow-card"
          data-testid="theme-picker-panel"
        >
          <div className="flex flex-col gap-2">
            <div className="text-kicker tracking-kicker uppercase font-mono text-muted">
              <FormattedMessage id="appearance__label" />
            </div>
            <AppearanceSwitch />
          </div>

          <div className="flex flex-col gap-2">
            <div className="text-kicker tracking-kicker uppercase font-mono text-muted">
              <FormattedMessage id="vibe__label" />
            </div>
            <VibeSwitch />
            <div className="text-explain text-muted">
              <FormattedMessage id="vibe__explain" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ThemePicker;
